import React, { useState } from "react";
import {
  Row,
  Col,
  Button,
  Badge,
  Dropdown,
  Modal,
  Alert,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import moment from "moment";
import { ClipLoader } from "react-spinners";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CopyToClipboard } from "react-copy-to-clipboard";

interface Webhook {
  id: string;
  url: string;
  events: string[];
  createdAt: string;
  contractId: number;
}

interface WebhookListProps {
  webhooks: Webhook[];
  onDelete: (webhookId: string) => Promise<void>;
  onTest: (webhookId: string, eventType: "bank" | "invoice") => Promise<void>;
  onRegenerateSecret: (webhookId: string) => Promise<void>;
  onCloseSecretModal: () => void;
  regeneratedSecret: { webhookId: string; secret: string } | null;
  loading: boolean;
}

const WebhookList: React.FC<WebhookListProps> = ({
  webhooks,
  onDelete,
  onTest,
  onRegenerateSecret,
  onCloseSecretModal,
  regeneratedSecret,
  loading,
}) => {
  const [testingWebhook, setTestingWebhook] = useState<string | null>(null);
  const [regeneratingSecret, setRegeneratingSecret] = useState<string | null>(
    null
  );
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = () => {
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <ClipLoader color="#0d6efd" size={50} />
      </div>
    );
  }

  if (webhooks.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">
          No webhooks found. Create one to get started.
        </p>
      </div>
    );
  }

  const handleTest = async (
    webhookId: string,
    eventType: "bank" | "invoice"
  ) => {
    setTestingWebhook(webhookId);
    try {
      await onTest(webhookId, eventType);
    } finally {
      setTestingWebhook(null);
    }
  };

  const handleRegenerateSecret = async (webhookId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to regenerate the webhook secret? This will invalidate the current secret."
      )
    ) {
      return;
    }
    setRegeneratingSecret(webhookId);
    try {
      await onRegenerateSecret(webhookId);
    } finally {
      setRegeneratingSecret(null);
    }
  };

  return (
    <>
      <Row className="mb-2 fw-bold">
        <Col>URL</Col>
        <Col>Events</Col>
        <Col>Created</Col>
        <Col>Actions</Col>
      </Row>
      {webhooks.map((webhook) => (
        <Row key={webhook.id} className="mb-2 align-items-center">
          <Col>
            <div className="d-flex gap-2">
              <div className="text-truncate" style={{ maxWidth: "300px" }}>
                {webhook.url}
              </div>
              <CopyToClipboard
                text={webhook.url}
                onCopy={() => {
                  setCopySuccess(true);
                  setTimeout(() => setCopySuccess(false), 2000);
                }}
              >
                <Button variant="outline-secondary" size="sm">
                  <FontAwesomeIcon icon="copy" className="mr-1" />
                  {copySuccess ? "Copied!" : "Copy URL"}
                </Button>
              </CopyToClipboard>
            </div>
          </Col>
          <Col>
            <div className="d-flex flex-wrap gap-1">
              {webhook.events.map((event) => (
                <Badge key={event} variant="info">
                  {event}
                </Badge>
              ))}
            </div>
          </Col>
          <Col>{moment(webhook.createdAt).format("MMM D, YYYY HH:mm")}</Col>
          <Col>
            <div className="d-flex gap-2">
              <Link
                to={`/webhooks/${webhook.id}/deliveries?webhookId=${webhook.id}`}
                className="btn btn-sm btn-outline-primary"
              >
                See Events
              </Link>
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  <FontAwesomeIcon icon="cog" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => handleTest(webhook.id, "bank")}
                    disabled={!!testingWebhook}
                  >
                    {testingWebhook === webhook.id ? (
                      <ClipLoader color="#000000" size={12} />
                    ) : (
                      "Send Test Webhook (bank data)"
                    )}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleTest(webhook.id, "invoice")}
                    disabled={!!testingWebhook}
                  >
                    {testingWebhook === webhook.id ? (
                      <ClipLoader color="#000000" size={12} />
                    ) : (
                      "Send Test Webhook (invoice data)"
                    )}
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    onClick={() => handleRegenerateSecret(webhook.id)}
                    disabled={!!regeneratingSecret}
                  >
                    {regeneratingSecret === webhook.id ? (
                      <ClipLoader color="#000000" size={12} />
                    ) : (
                      "Regenerate Secret"
                    )}
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to delete this webhook?"
                        )
                      ) {
                        onDelete(webhook.id);
                      }
                    }}
                    className="text-danger"
                  >
                    Delete Webhook
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Col>
        </Row>
      ))}

      <Modal
        show={!!regeneratedSecret}
        onHide={onCloseSecretModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>New Webhook Secret</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <Alert.Heading>Important!</Alert.Heading>
            <p className="mb-0">
              Make sure to copy this secret now. You won't be able to see it
              again!
            </p>
          </Alert>
          <div className="d-flex align-items-center gap-2">
            <code
              className="flex-grow-1 p-2 bg-light rounded"
              style={{ wordBreak: "break-all" }}
            >
              {regeneratedSecret?.secret}
            </code>
            <CopyToClipboard
              text={regeneratedSecret?.secret || ""}
              onCopy={handleCopy}
            >
              <Button variant="outline-primary" size="sm">
                <FontAwesomeIcon icon="copy" className="mr-1" />
                {copySuccess ? "Copied!" : "Copy"}
              </Button>
            </CopyToClipboard>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCloseSecretModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WebhookList;
